/**
 * Reusable email form component.
 * Manages active (focused), error (invalid), and success states on a form wrapper.
 * Submits the form via fetch to avoid a full page reload.
 *
 * @param {HTMLFormElement|Element} formEl - The form or wrapper element
 * @param {Object} [options] - Configuration
 * @param {string} [options.activeClass='active'] - Class when input is focused
 * @param {string} [options.errorClass='error'] - Class when input has invalid value
 * @param {string} [options.successClass='success'] - Class when form is submitted successfully
 * @param {string} [options.inputSelector='.email-form__input'] - Input selector
 * @returns {Function} - Cleanup/destroy function
 */
function initEmailForm(formEl, options = {}) {
  const {
    activeClass = "active",
    errorClass = "error",
    successClass = "success",
    inputSelector = ".email-form__input",
  } = options;

  // Clone the form element to remove any existing event listeners
  const clone = formEl.cloneNode(true);
  formEl.replaceWith(clone);
  formEl = clone;

  const form = formEl.querySelector("form");
  const input = formEl.querySelector(inputSelector);
  const validationErrorMessage = formEl.querySelector("[data-validation-error]");
  const serviceErrorMessage = formEl.querySelector("[data-service-error]");

  if (!input || !form) return () => {};

  function onFocus() {
    formEl.classList.add(activeClass);
    formEl.classList.remove(errorClass, successClass);
    serviceErrorMessage.style.display = "none";
  }

  function onBlur(event) {
    if (!event.target.value.length) {
      formEl.classList.remove(activeClass);
    }

    const isValid = event.target.checkValidity();
    if (!isValid && event.target.value.length) {
      formEl.classList.add(errorClass);
      validationErrorMessage.style.display = "block";
    } else {
      formEl.classList.remove(errorClass);
      validationErrorMessage.style.display = "none";
    }
  }

  async function onSubmit(event) {
    event.preventDefault();

    if (!input.checkValidity()) {
      formEl.classList.add(errorClass);
      return;
    }

    formEl.classList.remove(errorClass);

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
      });

      if (response.ok) {
        formEl.classList.add(successClass);
        input.value = "";
        document.cookie = "newsletter_subscribed=1; max-age=31536000; path=/; SameSite=Strict";
      } else {
        formEl.classList.add(errorClass);
        serviceErrorMessage.style.display = "block";
      }
    } catch {
      formEl.classList.add(errorClass);
    }
  }

  form.addEventListener("submit", onSubmit);
  input.addEventListener("focus", onFocus);
  input.addEventListener("blur", onBlur);

  return function destroy() {
    input.removeEventListener("focus", onFocus);
    input.removeEventListener("blur", onBlur);
    form.removeEventListener("submit", onSubmit);
  };
}

/**
 * Initialize all forms with data-email-form attribute.
 */
function initEmailForms() {
  const forms = document.querySelectorAll("[data-email-form]");
  return Array.from(forms).map((form) => initEmailForm(form));
}

document.addEventListener("DOMContentLoaded", initEmailForms);